import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePrefersReducedMotion } from '../theme/hooks';
import { Card, Skel, Tween } from './parts';
import { failed, Loaded } from './api';

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Page views per minute for the last hour, drawn edge to edge on a canvas with a glowing
 * head. New data morphs from the previous line; the head ring pulses while the tab is open.
 */
const Stream: React.FC<{ points: number[] }> = ({ points }) => {
  const cv = useRef<HTMLCanvasElement>(null);
  const reduce = usePrefersReducedMotion();
  const from = useRef<number[]>(points.map(() => 0));
  const shown = useRef<number[]>(from.current);
  const t0 = useRef(performance.now());

  useEffect(() => {
    from.current = shown.current.length === points.length ? shown.current : points.map(() => 0);
    t0.current = performance.now();
  }, [points]);

  useEffect(() => {
    const el = cv.current;
    if (!el) return undefined;
    const ctx = el.getContext('2d');
    if (!ctx) return undefined;
    let raf = 0;
    const size = () => {
      const dpr = window.devicePixelRatio || 1;
      el.width = Math.round(el.offsetWidth * dpr);
      el.height = Math.round(el.offsetHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(size) : undefined;
    ro?.observe(el);

    const draw = (now: number) => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const acc = css('--oa-accent') || '#78be20';
      const line = css('--oa-line-2');
      const card = css('--oa-card');
      const p = reduce ? 1 : Math.min(1, (now - t0.current) / 900);
      const e = 1 - Math.pow(1 - p, 3);
      const vals = points.map((v, i) => (from.current[i] ?? 0) + (v - (from.current[i] ?? 0)) * e);
      shown.current = vals;
      const max = Math.max(4, ...points) * 1.2;
      const head = 16;
      const dx = (w - head) / Math.max(1, vals.length - 1);
      const Y = (q: number) => h - 6 - (q / max) * (h - 24);
      const pts = vals.map((v, i) => [i * dx, Y(v)] as const);

      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = line;
      ctx.setLineDash([2, 5]);
      ctx.lineWidth = 1;
      [0.33, 0.66].forEach((g) => {
        ctx.beginPath();
        ctx.moveTo(0, h * g);
        ctx.lineTo(w, h * g);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      const hy = pts[pts.length - 1][1];
      const fill = ctx.createLinearGradient(0, 0, 0, h);
      fill.addColorStop(0, `${acc}55`);
      fill.addColorStop(1, `${acc}08`);
      ctx.beginPath();
      ctx.moveTo(0, h);
      pts.forEach(([x, y]) => ctx.lineTo(x, y));
      ctx.lineTo(w, hy);
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();

      const stroke = ctx.createLinearGradient(0, 0, w, 0);
      stroke.addColorStop(0, `${acc}00`);
      stroke.addColorStop(0.35, acc);
      stroke.addColorStop(1, acc);
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.shadowColor = acc;
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowBlur = 0;

      const [hx, hy2] = pts[pts.length - 1];
      const pulse = reduce ? 0 : (now / 900) % 1;
      ctx.beginPath();
      ctx.arc(hx, hy2, 4 + pulse * 10, 0, Math.PI * 2);
      ctx.fillStyle = `${acc}${Math.round((1 - pulse) * 60).toString(16).padStart(2, '0')}`;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx, hy2, 4, 0, Math.PI * 2);
      ctx.fillStyle = acc;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = card;
      ctx.stroke();

      if (!reduce) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [points, reduce]);

  return <canvas ref={cv} className="absolute inset-0 block h-full w-full" role="img" aria-label="Page views per minute, last hour" />;
};

export const LiveNowCard: React.FC<{ data: Loaded | null }> = ({ data }) => {
  const chat = data && !failed(data.chat) ? data.chat : null;
  const vis = data && !failed(data.visitors) && data.visitors.connected ? data.visitors : null;
  const perMinute = vis?.live.perMinute ?? [];
  const lastHour = perMinute.reduce((a, b) => a + b, 0);
  const openChats = chat ? chat.now.active + chat.now.waiting + chat.now.withTeam : 0;

  return (
    <Card i={2} area="live" className="overflow-hidden" title="Live on the site" actions={<Link to="/admin/livechat" className="oa-link">Chats<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></Link>}>
      {!data ? (
        <>
          <Skel className="h-[38px] w-[140px]" />
          <Skel className="h-3 w-[70%]" />
          <Skel className="min-h-[110px] flex-1" />
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="oa-big text-[38px]">
              <Tween value={vis ? vis.live.visitors5m : openChats} />
              <span className="oa-unit">{vis ? (vis.live.visitors5m === 1 ? 'visitor' : 'visitors') : openChats === 1 ? 'open chat' : 'open chats'}</span>
            </span>
            <span className="oa-chip is-good">Now</span>
          </div>
          <div className="oa-kv">
            {vis ? (
              <span>
                Last hour <b>{lastHour.toLocaleString('en-GB')}</b> page views
              </span>
            ) : null}
            <span>
              Chats open <b>{chat ? openChats : '–'}</b>
            </span>
            {chat && chat.now.waiting ? (
              <span className="text-[var(--oa-warn)]">
                Waiting <b className="!text-[var(--oa-warn)]">{chat.now.waiting}</b>
              </span>
            ) : null}
          </div>
          <div className="relative -mx-[22px] -mb-[22px] min-h-[110px] flex-1 max-[700px]:-mx-[18px] max-[700px]:-mb-[18px]">
            {vis ? (
              <Stream points={perMinute} />
            ) : (
              <p className="absolute inset-x-[22px] bottom-6 text-[12.5px] text-[var(--oa-faint)]">Visitor stream appears once PostHog is connected.</p>
            )}
          </div>
        </>
      )}
    </Card>
  );
};
